import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    useTheme,
    useMediaQuery,
    IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import toast from 'react-hot-toast';
import { analytics, WrapperStyles } from '../../shared/utils';
import { HOOPER_NEON_GREEN, HOOPER_BLUE } from '../../shared/constants';
import { fetchReferralMeta } from '../../shared/services';
import FooterOverlay from '../overlays/FooterOverlay';
import DynamicMeta from '../shared/DynamicMeta';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 600,
    bgcolor: '#16181A',
    boxShadow: 24,
    borderRadius: 2,
    textAlign: 'center',
    pb: 4,
};

const PublicReferralView = () => {
    const { referralCode } = useParams<{ referralCode: string }>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [platform, setPlatform] = useState<'ios' | 'android' | 'unknown'>(
        'unknown',
    );
    const [meta, setMeta] = useState<{
        username: string;
        title: string;
        description: string;
    } | null>(null);

    useEffect(() => {
        analytics.screen('Web/Public/Referral', {
            referralCode,
        });

        // Detect platform
        const userAgent = navigator.userAgent || navigator.vendor;
        if (/iPad|iPhone|iPod/.test(userAgent)) {
            setPlatform('ios');
        } else if (/android/i.test(userAgent)) {
            setPlatform('android');
        }

        // Fetch personalized meta for this referral code
        if (referralCode) {
            fetchReferralMeta(referralCode).then((data) => {
                if (data) setMeta(data);
            });
        }
    }, [referralCode]);

    const handleCopyCode = async () => {
        if (!referralCode) return;

        const fallbackCopy = (text: string) => {
            try {
                const el = document.createElement('textarea');
                el.value = text;
                el.style.position = 'fixed';
                el.style.left = '-9999px';
                document.body.appendChild(el);
                el.focus();
                el.select();
                const ok = document.execCommand('copy');
                document.body.removeChild(el);
                return ok;
            } catch {
                return false;
            }
        };

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(referralCode);
            } else {
                const ok = fallbackCopy(referralCode);
                if (!ok) throw new Error('copy-failed');
            }
            toast.success('Referral code copied!');
            analytics.track('Web/Public/Referral: copied_referral_code', {
                referralCode,
            });
        } catch (err) {
            console.error('Failed to copy referral code', err);
            toast.error('Unable to copy. Please copy manually.');
        }
    };

    const handleRedeemInApp = () => {
        if (!referralCode) return;

        const openUrl = `hooper://i/${encodeURIComponent(referralCode)}`;
        const fallbackUrl =
            platform === 'ios'
                ? 'https://apps.apple.com/us/app/hooper-basketball/id6458646707'
                : 'https://play.google.com/store/apps/details?id=com.builtdifferent.hooper';

        analytics.track('Web/Public/Referral: clicked_redeem_in_app', {
            referralCode,
            platform,
        });

        let didHide = false;
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') didHide = true;
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        window.location.href = openUrl;

        setTimeout(() => {
            document.removeEventListener(
                'visibilitychange',
                onVisibilityChange,
            );
            if (!didHide) window.location.href = fallbackUrl;
        }, 1800);
    };

    return (
        <Container
            maxWidth={false}
            sx={{
                minHeight: '100vh',
                flexDirection: 'column',
                display: 'flex',
                px: '0 !important',
            }}
        >
            <DynamicMeta
                title={meta?.title || 'Someone has invited you to join Hooper'}
                description={
                    meta?.description ||
                    'Join Hooper and get a free Pro trial. Download the app and enter the referral code to get started.'
                }
                url={`/i/${referralCode || ''}`}
            />
            <Box sx={WrapperStyles}>
                <Box style={{ marginTop: 100 }} />

                <Box sx={style}>
                    <img
                        src="/images/background-1.jpg"
                        alt="Hooper"
                        style={{
                            width: '100%',
                            height: '200px',
                            objectFit: 'cover',
                            borderTopLeftRadius: 4,
                            borderTopRightRadius: 4,
                        }}
                    />
                    <Box
                        sx={{
                            px: !isMobile ? 4 : 2,
                            pt: 2,
                        }}
                    >
                        <img
                            src="/logos/logo-white.svg"
                            alt="Hooper"
                            style={{
                                width: 150,
                                height: 'auto',
                                objectFit: 'contain',
                            }}
                        />
                        <Typography
                            sx={{
                                color: HOOPER_NEON_GREEN,
                                fontSize: !isMobile ? 24 : 20,
                                fontWeight: 'bold',
                                mt: 2,
                            }}
                        >
                            {meta?.username && meta.username !== 'Someone'
                                ? `${meta.username} has invited you to join Hooper!`
                                : "Let's Hoop together!"}
                        </Typography>
                        <Typography
                            sx={{
                                color: 'white',
                                fontSize: !isMobile ? 16 : 14,
                                mt: 1,
                                mb: 3,
                            }}
                        >
                            Use this referral code to unlock your Pro
                            Subscription
                        </Typography>

                        {/* Referral Code Display */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 3,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: 2,
                                p: 1.5,
                            }}
                        >
                            <TextField
                                value={referralCode || ''}
                                InputProps={{
                                    readOnly: true,
                                    style: {
                                        color: 'white',
                                        fontSize: !isMobile ? 20 : 18,
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                    },
                                }}
                                sx={{
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        textAlign: 'center',
                                        padding: '8px',
                                    },
                                }}
                            />
                            <IconButton
                                onClick={handleCopyCode}
                                sx={{
                                    color: HOOPER_NEON_GREEN,
                                    backgroundColor: 'rgba(2, 255, 141, 0.1)',
                                    '&:hover': {
                                        backgroundColor:
                                            'rgba(2, 255, 141, 0.2)',
                                    },
                                }}
                            >
                                <ContentCopyIcon />
                            </IconButton>
                        </Box>

                        {/* Redeem Button */}
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleRedeemInApp}
                            sx={{
                                backgroundColor: HOOPER_BLUE,
                                color: 'white',
                                fontSize: !isMobile ? 18 : 16,
                                fontWeight: 'bold',
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                mb: 2,
                                '&:hover': {
                                    backgroundColor: '#6050FF',
                                },
                            }}
                        >
                            Redeem in the app
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ my: 8 }} />
                <FooterOverlay />
            </Box>
        </Container>
    );
};

export default PublicReferralView;
